import { useState, useEffect } from 'react';
import { usePostHog } from 'src/lib/posthog';

/**
 * Special custom hook for PostHog feature flags that waits for flags to be loaded
 *
 * This is needed because PostHog feature flags are not loaded immediately after identification.
 * This hook waits for the flags to be loaded and then returns the flag value.
 *
 * @param flagKey - The feature flag key to check
 * @returns boolean | undefined - undefined until ready, then the actual flag value
 */
export const useFeatureFlag = (flagKey: string): boolean | undefined => {
  if (import.meta.env.VITE_ENABLE_ALL_FEATURE_FLAGS === 'true') {
    return true;
  }

  const posthog = usePostHog();
  const [isEnabled, setIsEnabled] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (!posthog) {
      setIsEnabled(undefined);
      return;
    }

    const handleFeatureFlags = () => {
      setIsEnabled(posthog.isFeatureEnabled(flagKey));
    };

    // Set up the listener for when feature flags are loaded
    posthog.onFeatureFlags(handleFeatureFlags);

    // Also check immediately in case flags are already loaded
    handleFeatureFlags();

    // Cleanup function
    return () => {
      // Note: PostHog doesn't have an offFeatureFlags method, but this is fine
      // as the listener will be cleaned up when the component unmounts
    };
  }, [posthog, flagKey]);

  return isEnabled;
};

/**
 * Like ``useFeatureFlag`` but returns the flag's value: the variant key for a
 * multivariate flag (e.g. an experiment's ``'control'`` / ``'test'``), or a
 * boolean for a plain flag. Use this for A/B tests — ``useFeatureFlag`` is
 * true for every variant, including control.
 *
 * @param flagKey - The feature flag key to check
 * @returns string | boolean | undefined - undefined until flags have loaded
 */
export const useFeatureFlagVariant = (flagKey: string): string | boolean | undefined => {
  if (import.meta.env.VITE_ENABLE_ALL_FEATURE_FLAGS === 'true') {
    return true;
  }

  const posthog = usePostHog();
  const [value, setValue] = useState<string | boolean | undefined>(undefined);

  useEffect(() => {
    if (!posthog) {
      setValue(undefined);
      return;
    }

    const handleFeatureFlags = () => {
      setValue(posthog.getFeatureFlag(flagKey));
    };

    const unsubscribe = posthog.onFeatureFlags(handleFeatureFlags);
    handleFeatureFlags();
    return unsubscribe;
  }, [posthog, flagKey]);

  return value;
};
